import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, Button, Label, Badge, CurrencyInput, ConversionProgress, ConversionSuccess } from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRightLeft,
  ArrowDown,
  Loader2,
  AlertCircle,
  Check,
  Clock,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { formatCurrency, formatUSDC } from "@/lib/utils";
import { companyApi } from "@/services/api";
import { ResultDialog } from "@/components/ui/result-dialog";
import { useTradeStatus } from "@/hooks/useTradeStatus";

interface DashboardData {
  balance: {
    availableBalance: number;
    usdcBalance: number;
    usdcLockedBalance: number;
  };
}

interface Quote {
  quoteId: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  rate: number;
  spread: number;
  expiresAt: string;
  provider: string;
}

interface ConversionResult {
  conversionId: string;
  status: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  exchangeRate: number;
  newBrldBalance: number;
  newUsdcBalance: number;
  burnTxHash?: string;
  mintTxHash?: string;
  tradeId?: string;
}

// Status config for badges
const TRADE_STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "success" | "destructive" | "outline"; icon: React.ReactNode }> = {
  PENDING: { label: "Pendente", variant: "secondary", icon: <Clock className="w-3 h-3" /> },
  PROCESSING: { label: "Processando", variant: "default", icon: <Loader2 className="w-3 h-3 animate-spin" /> },
  SETTLED: { label: "Liquidado", variant: "outline", icon: <Check className="w-3 h-3" /> },
  COMPLETED: { label: "Concluído", variant: "success", icon: <CheckCircle2 className="w-3 h-3" /> },
  FAILED: { label: "Falhou", variant: "destructive", icon: <XCircle className="w-3 h-3" /> },
  EXPIRED: { label: "Expirado", variant: "secondary", icon: <AlertTriangle className="w-3 h-3" /> },
};

export function CompanyConverterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialFrom = searchParams.get("from") || "BRL";
  const initialTo = searchParams.get("to") || "USDC";

  // Normaliza os parâmetros: BRLD = BRL (mesmo token)
  const normalizeFrom = initialFrom.toUpperCase() === "USDC" ? "USDC" : "BRL";
  const normalizeTo = initialTo.toUpperCase() === "USDC" ? "USDC" : "BRL";

  const [fromCurrency, setFromCurrency] = useState<"BRL" | "USDC">(normalizeFrom);
  const [toCurrency, setToCurrency] = useState<"BRL" | "USDC">(
    // Se origem e destino são iguais, inverte automaticamente
    normalizeFrom === normalizeTo
      ? (normalizeFrom === "BRL" ? "USDC" : "BRL")
      : normalizeTo
  );
  const [amount, setAmount] = useState("");
  const [amountCents, setAmountCents] = useState(0);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Estados para os novos componentes de progresso e sucesso
  const [showProgress, setShowProgress] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [conversionResult, setConversionResult] = useState<ConversionResult | null>(null);

  // Estados para erros
  const [showError, setShowError] = useState(false);
  const [errorData, setErrorData] = useState<{ title: string; message: string } | null>(null);
  const [quoteUnavailable, setQuoteUnavailable] = useState(false);

  // Trade status tracking
  const [activeConversionId, setActiveConversionId] = useState<string | null>(null);
  const [lastConversionData, setLastConversionData] = useState<{
    fromCurrency: string;
    toCurrency: string;
    fromAmount: number;
    toAmount: number;
  } | null>(null);

  // Callbacks para atualização automática de saldos
  const handleTradeCompleted = useCallback(() => {
    // Recarrega os dados quando a trade é concluída
    loadData();
  }, []);

  const handleTradeFailed = useCallback(() => {
    // Status is shown in the card, no additional toast needed
  }, []);

  const { status: tradeStatus, isPolling, startPolling, stopPolling } = useTradeStatus(
    activeConversionId,
    {
      pollingInterval: 5000,
      autoStopOnFinal: true,
      onCompleted: handleTradeCompleted,
      onFailed: handleTradeFailed,
    }
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await companyApi.dashboard.get();
      setDashboardData(data as DashboardData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);


  const availableBrlCents = dashboardData?.balance?.availableBalance || 0;
  const usdcLockedCents = dashboardData?.balance?.usdcLockedBalance || 0;
  const availableUsdcCents = (dashboardData?.balance?.usdcBalance || 0) - usdcLockedCents;

  const maxAmount = fromCurrency === "BRL" ? availableBrlCents : availableUsdcCents;
  const maxAmountValue = maxAmount / 100;

  const handleAmountChange = (value: string, cents: number) => {
    setAmount(value);
    setAmountCents(cents);
    setQuote(null); // Limpa cotação ao alterar valor
    setQuoteUnavailable(false); // Reseta estado de indisponibilidade
  };

  const handleSetMax = () => {
    // Formata o valor máximo em centavos para string formatada
    const formatted = maxAmountValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setAmount(formatted);
    setAmountCents(maxAmount);
    setQuote(null);
    setQuoteUnavailable(false);
  };

  const handleSwapCurrencies = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setAmount("");
    setAmountCents(0);
    setQuote(null);
    setQuoteUnavailable(false);
  };

  const handleGetQuote = async () => {
    if (amountCents < 100) return;

    setLoadingQuote(true);
    setQuoteUnavailable(false);
    try {
      const quoteData = await companyApi.conversion.getQuote(fromCurrency, toCurrency, amountCents);
      setQuote(quoteData);
    } catch (error: any) {
      // Mostra o estado de cotação indisponível em vez de dialog de erro
      setQuoteUnavailable(true);
    } finally {
      setLoadingQuote(false);
    }
  };

  const handleConvert = async () => {
    if (!quote) return;

    setSubmitting(true);
    setShowProgress(true);

    try {
      const result = await companyApi.conversion.convert(quote.quoteId) as ConversionResult;

      // Store conversion data for status card
      setLastConversionData({
        fromCurrency: result.fromCurrency,
        toCurrency: result.toCurrency,
        fromAmount: result.fromAmount,
        toAmount: result.toAmount,
      });

      // If we have a trade ID, start polling for status
      if (result.tradeId) {
        setActiveConversionId(result.conversionId);
        // Start polling after a short delay to allow the trade to be processed
        setTimeout(() => startPolling(), 1000);
      }

      // Armazena o resultado e mostra tela de sucesso
      setConversionResult(result);
      setShowProgress(false);
      setShowSuccess(true);

      // Limpa o formulário
      setAmount("");
      setAmountCents(0);
      setQuote(null);

      // Recarrega os saldos automaticamente
      loadData();
    } catch (error: any) {
      setShowProgress(false);
      setErrorData({
        title: "Erro na conversão",
        message: error.message || "Não foi possível realizar a conversão. Tente novamente.",
      });
      setShowError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismissStatus = () => {
    stopPolling();
    setActiveConversionId(null);
    setLastConversionData(null);
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setConversionResult(null);
  };

  const handleViewBalance = () => {
    setShowSuccess(false);
    setConversionResult(null);
    navigate(toCurrency === "USDC" ? "/usdc" : "/brld");
  };

  // Verifica se a cotação expirou (mas não durante o processamento)
  const isQuoteExpired = quote && !submitting && new Date(quote.expiresAt) < new Date();

  // Tempo restante da cotação
  const getQuoteTimeRemaining = () => {
    if (!quote) return 0;
    const remaining = Math.floor((new Date(quote.expiresAt).getTime() - Date.now()) / 1000);
    return Math.max(0, remaining);
  };

  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    // Não atualiza o timer se estiver processando a conversão
    if (quote && !submitting) {
      setTimeRemaining(getQuoteTimeRemaining());
      const interval = setInterval(() => {
        // Verifica novamente se não está submitting antes de atualizar
        if (submitting) return;

        const remaining = getQuoteTimeRemaining();
        setTimeRemaining(remaining);
        if (remaining <= 0) {
          setQuote(null);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [quote, submitting]);

  const currentTradeStatusConfig = tradeStatus?.status ? TRADE_STATUS_CONFIG[tradeStatus.status] : null;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<ArrowRightLeft className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Converter"
        description="Converta entre BRLD e USDC"
        stats={[
          {
            value: loading ? "..." : formatCurrency(availableBrlCents / 100),
            label: "BRLD Disponível",
          },
          {
            value: loading ? "..." : formatUSDC(availableUsdcCents / 100),
            label: "USDC Disponível",
          },
        ]}
      />

      <div className="max-w-xl mx-auto space-y-4">
        {/* Card de status da trade (se houver conversão ativa) */}
        {activeConversionId && tradeStatus && lastConversionData && (
          <Card className={
            tradeStatus.status === "COMPLETED" ? "border-green-500 bg-green-50" :
            tradeStatus.status === "FAILED" ? "border-red-500 bg-red-50" :
            "border-blue-500 bg-blue-50"
          }>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">
                  Status da Conversão
                </CardTitle>
                {currentTradeStatusConfig && (
                  <Badge variant={currentTradeStatusConfig.variant} className="flex items-center gap-1">
                    {currentTradeStatusConfig.icon}
                    {currentTradeStatusConfig.label}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Conversão</span>
                <span className="font-medium">
                  {lastConversionData.fromCurrency === "BRL"
                    ? formatCurrency(lastConversionData.fromAmount / 100)
                    : formatUSDC(lastConversionData.fromAmount / 100)}
                  {" → "}
                  {lastConversionData.toCurrency === "USDC"
                    ? formatUSDC(lastConversionData.toAmount / 100)
                    : formatCurrency(lastConversionData.toAmount / 100)}
                </span>
              </div>

              {isPolling && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>Aguardando liquidação...</span>
                </div>
              )}

              {tradeStatus.status === "COMPLETED" && (
                <p className="text-sm text-green-700">
                  Sua conversão foi liquidada com sucesso pelo provedor de liquidez.
                </p>
              )}

              {tradeStatus.status === "FAILED" && (
                <p className="text-sm text-red-700">
                  {tradeStatus.errorMessage || "Houve um problema com a liquidação. Entre em contato com o suporte."}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={handleDismissStatus}>
                  Fechar
                </Button>
                {(tradeStatus.status === "COMPLETED" || tradeStatus.status === "FAILED") && (
                  <Button
                    size="sm"
                    onClick={() => navigate(lastConversionData.toCurrency === "USDC" ? "/usdc" : "/brld")}
                  >
                    Ver Saldo
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Card de conversão */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            {/* De */}
            <div className="space-y-2">
              <Label>De</Label>
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold">
                      {fromCurrency === "BRL" ? "R$" : "$"}
                    </div>
                    <span className="font-medium">
                      {fromCurrency === "BRL" ? "BRLD (Disponível)" : "USDC"}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Saldo:{" "}
                    {fromCurrency === "BRL"
                      ? formatCurrency(availableBrlCents / 100)
                      : formatUSDC(availableUsdcCents / 100)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CurrencyInput
                    currency={fromCurrency === "BRL" ? "BRL" : "USD"}
                    value={amount}
                    onChange={handleAmountChange}
                    className="text-xl font-medium h-12"
                  />
                  <Button variant="outline" size="sm" onClick={handleSetMax} className="flex-shrink-0">
                    Máximo
                  </Button>
                </div>
              </div>
            </div>

            {/* Botão de swap */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="icon"
                className="rounded-full"
                onClick={handleSwapCurrencies}
              >
                <ArrowDown className="w-4 h-4" />
              </Button>
            </div>

            {/* Para */}
            <div className="space-y-2">
              <Label>Para</Label>
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold">
                      {toCurrency === "BRL" ? "R$" : "$"}
                    </div>
                    <span className="font-medium">
                      {toCurrency === "BRL" ? "BRLD (Disponível)" : "USDC"}
                    </span>
                  </div>
                </div>
                <div className="text-xl font-medium">
                  {quote
                    ? toCurrency === "USDC"
                      ? formatUSDC(quote.toAmount / 100)
                      : formatCurrency(quote.toAmount / 100)
                    : "—"}
                </div>
              </div>
            </div>

            {/* Detalhes da cotação */}
            {quote && !isQuoteExpired && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-700">Cotação</span>
                  <span className="font-medium text-blue-900">
                    1 USDC = R$ {quote.rate.toFixed(4)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-700 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Expira em
                  </span>
                  <span className="font-medium text-blue-900">
                    {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, "0")}
                  </span>
                </div>
              </div>
            )}

            {/* Aviso se converter BRLD para USDC */}
            {fromCurrency === "BRL" && toCurrency === "USDC" && amountCents > 0 && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  USDC não rende. Para rentabilizar, mantenha em BRLD e aplique.
                </p>
              </div>
            )}

            {/* Aviso de cotação indisponível */}
            {quoteUnavailable && (
              <div className="flex flex-col items-center justify-center p-4 bg-amber-50 rounded-lg border border-amber-200 text-center">
                <AlertCircle className="w-6 h-6 text-amber-600 mb-2" />
                <p className="text-sm text-amber-800 font-medium">Cotação temporariamente indisponível</p>
                <p className="text-xs text-amber-700 mt-1">Não é possível realizar conversões no momento</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 text-amber-700 border-amber-300 hover:bg-amber-100"
                  onClick={handleGetQuote}
                  disabled={loadingQuote}
                >
                  {loadingQuote ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1" />
                  )}
                  Tentar novamente
                </Button>
              </div>
            )}

            {/* Botões */}
            {!quoteUnavailable && (!quote || isQuoteExpired) && (
              <Button
                className="w-full h-12"
                onClick={handleGetQuote}
                disabled={loadingQuote || amountCents < 100 || amountCents > maxAmount}
              >
                {loadingQuote ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {isQuoteExpired ? "Atualizar Cotação" : "Obter Cotação"}
              </Button>
            )}
            {!quoteUnavailable && quote && !isQuoteExpired && (
              <Button
                className="w-full h-12"
                onClick={handleConvert}
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Confirmar Conversão
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Importante</p>
                <p>
                  Apenas o saldo BRLD <strong>Disponível</strong> pode ser convertido. Se você tem
                  saldo Aplicado, primeiro resgate a aplicação para liberar o saldo.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal de progresso da conversão */}
      <ConversionProgress
        open={showProgress}
        fromCurrency={fromCurrency}
        toCurrency={toCurrency}
        fromAmount={amountCents}
        estimatedToAmount={quote?.toAmount || 0}
      />

      {/* Modal de sucesso com resumo */}
      {conversionResult && (
        <ConversionSuccess
          open={showSuccess}
          onClose={handleCloseSuccess}
          onViewBalance={handleViewBalance}
          fromCurrency={conversionResult.fromCurrency as "BRL" | "USDC"}
          toCurrency={conversionResult.toCurrency as "BRL" | "USDC"}
          fromAmount={conversionResult.fromAmount}
          toAmount={conversionResult.toAmount}
          exchangeRate={conversionResult.exchangeRate}
          newBrldBalance={conversionResult.newBrldBalance}
          newUsdcBalance={conversionResult.newUsdcBalance}
          burnTxHash={conversionResult.burnTxHash}
          mintTxHash={conversionResult.mintTxHash}
        />
      )}

      {/* Dialog de erro */}
      <ResultDialog
        open={showError}
        onClose={() => setShowError(false)}
        variant="error"
        title={errorData?.title || ""}
        description={errorData?.message || ""}
      />
    </div>
  );
}
